<template>
  <div ref="outerContainer" class="tw-relative tw-px-4">
    <div
      ref="navigation"
      class="
        tw-sticky
        tw-bg-white
        tw-top-0
        tw-flex
        tw-justify-between
        tw-pb-3
        tw-py-3
        tw-z-10
        tw-gap-4
      "
    >
      <sf-button-icon icon="arrow-left" @click="drawerStore.closeDrawer" />
      <div class="tw-flex-1">
        <overview-navigation-search
          :can-be-focused="true"
          data-testid="overview-search"
        />
      </div>
    </div>
    <div class="tw-relative tw-z-0">
      <overview-navigation
        :all-expanded-groups="allExpandedGroups"
        :overview-tree="overviewTreeData"
        :is-loading="loading"
        :ctrl-click="ctrlClick"
        @item-clicked="treeMenuItemClicked"
        @item-expanded="handleItemExpansion"
        @item-toggled="handleItemToggled"
      >
        <template #accent="{ itemForSlot }">
          <overview-navigation-active-highlight
            :highlighted-id="highlightedId"
            :highlighted-type="highlightedType"
            :item-id="
              itemForSlot.type === 'account_entry'
                ? itemForSlot.accountEntryId
                : itemForSlot.id
            "
            :item-type="itemForSlot.type"
            :scroll-into-view-timestamp="scrollIntoViewTimestamp"
            @scroll-offset-element="handleScrollOffsetElement"
          />
        </template>
        <template #itemSlot="{ itemForSlot }">
          <div class="sf-tree-menu__item-icon">
            <overview-navigation-pulse-reconciliation-status
              v-if="isPulseUser"
              :item="itemForSlot"
            />
            <overview-navigation-checkmark
              v-if="isInternalUser"
              :item-id="
                itemForSlot.type === 'account_entry'
                  ? itemForSlot.accountEntryId
                  : itemForSlot.id
              "
            />
          </div>
        </template>
      </overview-navigation>
    </div>
  </div>
</template>
<script setup lang="ts">
import type {
  WorkflowOverviewItem,
  WorkflowOverviewItemType,
  WorkflowOverviewGroup,
  UserType,
} from '@app/application/workflow-overview-navigation/types'
import type { WorkflowFilterStatus } from '@app/shared/types/sf-data'

import isEqual from 'lodash/isEqual'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeUnmount,
  nextTick,
  ref,
  watch,
  watchEffect,
} from 'vue'
import { useMediaQuery } from '@vueuse/core'

import OverviewNavigation from './overview-navigation.vue'
import useOverviewMenu from '../composables/use-overview-menu-click'
import { determineFilterValue } from '../utils'
import OverviewNavigationActiveHighlight from './overview-navigation-active-highlight.vue'
import OverviewNavigationCheckmark from './overview-navigation-checkmark.vue'
import OverviewNavigationPulseReconciliationStatus from './overview-navigation-pulse-reconciliation-status.vue'
import OverviewNavigationSearch from './overview-navigation-search.vue'
import { SfLog } from '@app/adapters/logger'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button/index'

import useSfPolling from '@app/shared/vue/composables/use-sf-polling'
import { useWorkflowOverview } from '@app/application/workflow-overview-navigation/store/overview'
import { useWorkflowReviewFilter } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-filter'
import { useWorfklowReviewItems } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-items'
import { useDrawer } from '@app/shared/pinia/drawer'
import { UserTypes } from '../types'
import { usePulseWorkflowProgress } from '../store/pulse-workflow-progress'

defineOptions({
  name: 'OverviewNavigationDrawer',
})

interface Props {
  allowedFilterValues?: WorkflowFilterStatus[]
  // In WF Review show-all means starred or remarked on
  defaultFilterParamValue?: WorkflowFilterStatus
  dependsOnReviewStore: boolean
  highlightedId?: number
  highlightedType?: WorkflowOverviewItemType
  ledgerId: number
  messageBusChannel?: string | null
  messageBusLastId?: string
  userType?: UserType
  workflowId: number
  ctrlClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowedFilterValues: () => [
    'show-all',
    'todos',
    'checks',
    'unreconciled',
    'unreviewed_by_me',
    'starred',
  ],
  defaultFilterParamValue: 'show-all',
  dependsOnReviewStore: false,
  highlightedId: undefined,
  highlightedType: undefined,
  messageBusChannel: null,
  messageBusLastId: '',
  userType: UserTypes.INTERNAL_USER,
})

const lastFetchedWorkflowId = ref(0)
const hasSetActiveItemOnce = ref(false)
const loading = ref(true)
const navigation = ref<HTMLElement | null>(null)
const outerContainer = ref<HTMLElement | null>(null)
const scrollableContainer = ref<HTMLElement | null>(null)
const requestedData = ref(false)
const scrollIntoViewTimestamp = ref(0)
const sessionKey = ref()

const { startPolling } = useSfPolling()
const drawerStore = useDrawer()
const filterStore = useWorkflowReviewFilter()
const overviewStore = useWorkflowOverview()
const pulseWorkflowProgressStore = usePulseWorkflowProgress()
const reviewItemsStore = useWorfklowReviewItems()

const { treeMenuItemClicked: scrollToTreeMenuItem } = useOverviewMenu()

const { isLeftDrawerOpen } = storeToRefs(drawerStore)
const { filter } = storeToRefs(filterStore)
const { allExpandedGroups, filteredGroupsById, overviewTreeData } =
  storeToRefs(overviewStore)
const { currentReviewItem } = storeToRefs(reviewItemsStore)

let unsubscribeHandler: null | (() => void)

const isPulseUser = computed<boolean>(
  () => props.userType === UserTypes.PULSE_USER
)
const isInternalUser = computed<boolean>(
  () => props.userType === UserTypes.INTERNAL_USER
)

if (isPulseUser.value) {
  if (props.messageBusChannel) {
    setupSubscription(props.messageBusChannel, props.messageBusLastId)
  }

  const isLargeScreen = useMediaQuery('(min-width: 1280px)')
  // For Pulse users:
  //   - On Desktop: open left sidepane IF the user doesn't have any preference set
  //   - On Mobile: force-close the drawer, regardless of previous user preference
  const drawerFn = isLargeScreen.value
    ? drawerStore.openLeftDrawerDefault
    : drawerStore.closeDrawer

  window.requestAnimationFrame(drawerFn)
}

window.eventHub.$on('sf-left-overview-navigation:reload', fetchInitialData)

onBeforeUnmount(() => {
  window.eventHub.$off('sf-left-overview-navigation:reload', fetchInitialData)
  removeSubscription()
})

async function fetchInitialData() {
  loading.value = true
  requestedData.value = true
  lastFetchedWorkflowId.value = props.workflowId

  const filterParam = determineFilterValue({
    incomingFilter: filter.value,
    allowedFilterValues: props.allowedFilterValues,
    defaultFilterValue: props.defaultFilterParamValue,
  })

  sessionKey.value = `${window.Silverfin.currentFirmId}:${
    window.Silverfin.currentCompanyId
  }:${window.Silverfin.currentLedgerId()}:${props.workflowId}`

  try {
    await overviewStore.fetchOverview({
      filter: filterParam,
      isPulseUser: isPulseUser.value,
      workflowId: props.workflowId,
      ledgerId: props.ledgerId,
      includeReviewStatus: !props.dependsOnReviewStore,
      highlightedItemId: props.highlightedId ?? undefined,
      highlightedItemType: props.highlightedType ?? undefined,
    })
    loadExpandedState(sessionKey.value)
  } catch {
    SfLog.error(
      `Failed to fetch overview tree for workflow ${props.workflowId} and ledger ${props.ledgerId}`
    )
  }

  loading.value = false
}

/**
 * After the parents expand we send a signal to the highlight component so that it sends back
 * its top offset. This way we know how much we need to scroll to make the highlighted item visible.
 */
function forceHighlightedItemIntoView() {
  nextTick(() => {
    scrollIntoViewTimestamp.value = Date.now()
  })
}

async function handleScrollOffsetElement(
  targetElement: HTMLElement
): Promise<void> {
  if (scrollableContainer.value) {
    await nextTick()
    await nextTick()
    if (scrollableContainer.value) {
      const spaceBetween =
        targetElement.getBoundingClientRect().top -
        scrollableContainer.value.getBoundingClientRect().top +
        scrollableContainer.value.scrollTop

      scrollableContainer.value.scrollTo({
        top: spaceBetween - 80,
      })

      scrollIntoViewTimestamp.value = 0
      hasSetActiveItemOnce.value = true
    }
  }
}

function handleItemToggled(groupId: string): void {
  overviewStore.toggleOverviewGroup(groupId.toString())
  saveExpandedStateToSession(
    sessionKey.value,
    JSON.stringify(allExpandedGroups.value)
  )
}

/**
 * The tree menu itself notifies that it wants some parents expanded.
 * This is happening because it contains an item that is highlighted and we need to expand the hierarchy until that item.
 * @param itemId - The group id of one of the highlighted item's ancestors.
 */
function handleItemExpansion(itemId: number): void {
  overviewStore.expandOverviewGroup(itemId.toString())

  forceHighlightedItemIntoView()
}

function treeMenuItemClicked(
  item: WorkflowOverviewItem | WorkflowOverviewGroup
) {
  if (!props.dependsOnReviewStore) {
    const workflowItem = item as WorkflowOverviewItem
    const path = workflowItem.path
    if (path) {
      window.Turbolinks.visit(path)
    }
  } else {
    scrollToTreeMenuItem(item)
  }
}

function saveExpandedStateToSession(key: string, expandedGroups: string) {
  sessionStorage.setItem(key, expandedGroups)
}

function loadExpandedState(key: string) {
  const sessionInfo = sessionStorage.getItem(key)
  if (sessionInfo !== null) {
    const expandGroupsFromSession = JSON.parse(sessionInfo)
    Object.entries(expandGroupsFromSession).forEach(([groupId, isToggled]) => {
      if (isToggled) {
        overviewStore.expandOverviewGroup(groupId)
      }
    })
  } else {
    overviewStore.expandHigherOrderGroups()
    overviewStore.expandTopLevelGroups()
  }
}

function setupSubscription(
  messageBusChannel: string,
  messageBusLastId: string
): void {
  removeSubscription()

  unsubscribeHandler = startPolling(
    messageBusChannel,
    parseInt(messageBusLastId, 10),
    () => true,
    pulseWorkflowProgressStore.receivePulseWorkflowProgress
  )
}

function removeSubscription(): void {
  if (unsubscribeHandler) {
    unsubscribeHandler()
    unsubscribeHandler = null
  }
}

watch(
  outerContainer,
  (newValue) => {
    if (newValue) {
      scrollableContainer.value = newValue.closest('aside')
    }
  },
  { immediate: true }
)

watch(isLeftDrawerOpen, (newValue, oldValue) => {
  // Each time the drawer closes, reset the flag
  if (!newValue && newValue !== oldValue) {
    hasSetActiveItemOnce.value = false
  }
})

watch(filteredGroupsById, (newValue, oldValue) => {
  if (
    Object.keys(newValue).length > 0 &&
    newValue !== oldValue &&
    !isEqual(newValue, oldValue)
  ) {
    Object.keys(filteredGroupsById.value).forEach((group) => {
      overviewStore.expandOverviewGroup(group)
    })
  }
})

watchEffect(() => {
  // In WF Review, the first time the drawer opens, expand and focus the current review item
  if (
    props.dependsOnReviewStore &&
    !hasSetActiveItemOnce.value &&
    currentReviewItem.value &&
    isLeftDrawerOpen.value &&
    overviewTreeData.value.length > 0
  ) {
    overviewStore.expandItemParents(
      currentReviewItem.value.id,
      currentReviewItem.value.itemClass
    )

    forceHighlightedItemIntoView()
  }
})

watchEffect(() => {
  // Reloaded in dev HMR mode / browser back button, no need to request data again
  if (overviewTreeData.value.length > 0) {
    requestedData.value = true
    loading.value = false
  }

  // Force data fetch when the browser back button is used to navigate to a different workflow
  if (
    overviewTreeData.value.length > 0 &&
    lastFetchedWorkflowId.value !== props.workflowId &&
    isLeftDrawerOpen.value
  ) {
    fetchInitialData()
  }

  // Opened for the first time while in current page
  if (
    overviewTreeData.value.length === 0 &&
    isLeftDrawerOpen.value &&
    !requestedData.value
  ) {
    fetchInitialData()
  }
})
</script>
